import React, { useState } from 'react';
import { BookOpen, Newspaper, Apple, Pill, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

// Sample educational content following strict non-clinical guidelines
const EDUCATION_CONTENT = {
  'research-news': [
    {
      id: 1,
      title: 'Understanding Blood Cancer Research',
      summary: 'An overview of how blood cancer research works and the stages of clinical trials.',
      content: `Blood cancer research includes laboratory studies, clinical trials, and long-term outcome studies. Research helps scientists understand how blood cancers develop and how treatments might work.

Clinical trials are conducted in phases (Phase 1, 2, 3, and 4), each with different goals. Participation in research is always voluntary.

Your healthcare team can discuss whether any research studies might be relevant to your situation. Decisions about participation should be based on your individual circumstances and guided by your medical team.`,
    },
    {
      id: 2,
      title: 'How Blood Tests Are Used',
      summary: 'General information about common blood tests and what they measure.',
      content: `Blood tests measure various components in your blood, including different types of cells and proteins. Common measurements include:

• White Blood Cells (WBC): A type of blood cell involved in immune function
• Lymphocytes: A specific type of white blood cell
• Platelets: Cells involved in blood clotting
• Haemoglobin: A protein that carries oxygen in red blood cells
• Neutrophils: Another type of white blood cell

Your doctor interprets these results in the context of your individual health history and symptoms. What's considered normal can vary between individuals, and only your healthcare team can determine what your specific results mean for you.`,
    },
  ],
  'nutrition-wellbeing': [
    {
      id: 3,
      title: 'General Nutrition Information',
      summary: 'Overview of basic nutrition principles for general wellbeing.',
      content: `Good nutrition involves eating a variety of foods from different food groups. This generally includes:

• Fruits and vegetables
• Whole grains
• Protein sources (meat, fish, beans, legumes)
• Dairy or dairy alternatives
• Healthy fats

Individual nutritional needs vary based on many factors including age, activity level, and health conditions. If you have a blood cancer diagnosis, your nutritional needs may differ from general guidelines.

Always consult your healthcare team or a registered dietitian before making significant dietary changes, especially if you are undergoing treatment or have specific health conditions.`,
    },
    {
      id: 4,
      title: 'Understanding Fatigue',
      summary: 'General information about fatigue and when to seek medical advice.',
      content: `Fatigue is a feeling of tiredness or exhaustion that doesn't improve with rest. It can have many causes, including:

• Medical conditions
• Medications
• Sleep problems
• Stress or anxiety
• Nutritional factors

Fatigue associated with blood cancers can be different from ordinary tiredness. It may be persistent and not relieved by sleep or rest.

If you're experiencing persistent fatigue, it's important to discuss this with your healthcare team. They can help identify the cause and suggest appropriate management strategies based on your individual situation.`,
    },
  ],
  'vitamins-supplements': [
    {
      id: 5,
      title: 'Vitamin D - General Information',
      summary: 'What vitamin D is and how the body uses it.',
      content: `Vitamin D is a nutrient that helps the body absorb calcium and maintain bone health. It also plays a role in immune function. The body can produce vitamin D when skin is exposed to sunlight.

Sources of vitamin D include:
• Sunlight exposure
• Certain foods (fatty fish, egg yolks, fortified dairy products)
• Supplements (when recommended by a doctor)

Vitamin D levels can be measured through blood tests. Whether you need vitamin D supplementation depends on your individual blood test results and health status.

**Important:** Never start taking vitamin D supplements or change your dose without consulting your healthcare team. They can interpret your blood test results and advise on appropriate next steps based on your individual needs.`,
    },
    {
      id: 6,
      title: 'Vitamin B12 - General Information',
      summary: 'What vitamin B12 is and its role in the body.',
      content: `Vitamin B12 is a nutrient that helps keep nerve cells and blood cells healthy. It's also involved in DNA production. The body cannot produce B12, so it must come from food or supplements.

Natural sources of B12 include:
• Meat, poultry, and fish
• Dairy products
• Eggs
• Fortified foods (some cereals and plant-based milks)

Some people may have difficulty absorbing B12 from food. This can be identified through blood tests.

**Important:** Decisions about B12 supplementation should be guided by blood test results and medical advice from your healthcare team. Do not start B12 supplements without consulting your doctor, especially if you have a blood cancer diagnosis.`,
    },
    {
      id: 7,
      title: 'Understanding Supplements',
      summary: 'General information about dietary supplements and important considerations.',
      content: `Dietary supplements include vitamins, minerals, herbs, and other substances. They are meant to supplement (add to) the diet, not replace food or medical treatment.

**Important considerations:**
• Supplements can interact with medications
• Some supplements may not be safe during cancer treatment
• "Natural" does not always mean "safe"
• Supplement quality and purity can vary
• Some supplements may affect blood test results

**For people with blood cancers:**
Certain supplements may interfere with treatments or affect blood counts. Always discuss any supplements you're considering with your healthcare team before taking them.

Never use supplements as a substitute for medical treatment or change your prescribed medications without consulting your doctor.`,
    },
  ],
};

const CATEGORY_INFO = {
  'research-news': {
    title: 'Research & News',
    description: 'General information about blood cancer research and medical testing',
    icon: Newspaper,
  },
  'nutrition-wellbeing': {
    title: 'Nutrition & Wellbeing',
    description: 'General information about nutrition and wellbeing',
    icon: Apple,
  },
  'vitamins-supplements': {
    title: 'Vitamins & Supplements (Information Only)',
    description: 'General information about vitamins and supplements - not recommendations',
    icon: Pill,
  },
};

const ArticleCard = ({ article, onRead }) => {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 hover:border-primary/50 transition-colors cursor-pointer"
         onClick={() => onRead(article)}>
      <h3 className="text-xl font-semibold text-slate-900 mb-2">{article.title}</h3>
      <p className="text-slate-600 mb-4">{article.summary}</p>
      <button className="text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1">
        Read More
        <ChevronDown className="h-4 w-4" />
      </button>
    </div>
  );
};

const ArticleView = ({ article, onBack }) => {
  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-primary hover:text-primary/80 mb-6 font-medium"
      >
        <ChevronUp className="h-4 w-4" />
        Back to Articles
      </button>
      
      <h2 className="text-3xl font-bold text-slate-900 mb-6">{article.title}</h2>
      
      {/* Article content - fully free, no gating */}
      <div className="prose prose-slate max-w-none">
        {article.content.split('\n\n').map((paragraph, idx) => (
          <p key={idx} className="text-slate-700 leading-relaxed mb-4">
            {paragraph}
          </p>
        ))}
      </div>
      
      {/* Article Disclaimer */}
      <div className="mt-8 pt-6 border-t border-slate-200">
        <p className="text-sm text-slate-600 italic">
          This information is general in nature and not medical advice.
        </p>
      </div>
    </div>
  );
};

const EducationPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('research-news');
  const [selectedArticle, setSelectedArticle] = useState(null);

  const categoryInfo = CATEGORY_INFO[selectedCategory];
  const CategoryIcon = categoryInfo.icon;
  const articles = EDUCATION_CONTENT[selectedCategory];

  // Handle article selection - for free users, always show to trigger gate
  const handleArticleSelect = (articleId) => {
    setSelectedArticle(selectedArticle === articleId ? null : articleId);
  };

  return (
    <div className="space-y-6">
      {/* Header - Always visible */}
      <div className="flex items-center gap-3 mb-2">
        <BookOpen className="h-8 w-8 text-primary" />
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900">
          Information & Education
        </h1>
      </div>
      <p className="text-lg leading-relaxed text-slate-700">
        General health information for educational purposes
      </p>

      {/* Top Banner - Section Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-amber-900 mb-2">General Information Only</h2>
            <p className="text-amber-800 leading-relaxed">
              Content in this section is for general education and awareness. It does not provide medical advice 
              and should not be used to make treatment or supplement decisions. Always consult your healthcare 
              team for personalized medical guidance.
            </p>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white rounded-2xl p-2 border border-slate-200 shadow-sm">
        <div className="grid md:grid-cols-3 gap-2">
          {Object.entries(CATEGORY_INFO).map(([key, info]) => {
            const Icon = info.icon;
            return (
              <button
                key={key}
                onClick={() => {
                  setSelectedCategory(key);
                  setSelectedArticle(null);
                }}
                className={`p-4 rounded-xl text-left transition-all ${
                  selectedCategory === key
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Icon className="h-5 w-5" />
                  <h3 className="font-semibold">{info.title}</h3>
                </div>
                <p className={`text-sm ${selectedCategory === key ? 'text-white/90' : 'text-slate-600'}`}>
                  {info.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      {selectedArticle ? (
        <ArticleView article={selectedArticle} onBack={() => setSelectedArticle(null)} />
      ) : (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <CategoryIcon className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-semibold text-slate-900">{categoryInfo.title}</h2>
          </div>
          
          <div className="grid gap-4">
            {articles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                onRead={setSelectedArticle}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EducationPage;
